/**
 * UsersRoute — Main page component for the Crew Route Builder.
 *
 * Orchestrates the stop list, detail panel, and add-stop modal.
 * Supports both user mode (auto-resolves crew) and admin mode (crew picker).
 */

import React, { useState, useEffect, useCallback } from 'react'
import { connect } from 'react-redux'
import { useLocation } from 'react-router-dom'
import axios from 'axios'
import { makeStyles } from '@material-ui/core/styles'
import Paper from '@material-ui/core/Paper'
import Typography from '@material-ui/core/Typography'
import Button from '@material-ui/core/Button'
import Chip from '@material-ui/core/Chip'
import CircularProgress from '@material-ui/core/CircularProgress'
import Snackbar from '@material-ui/core/Snackbar'
import Alert from '@material-ui/lab/Alert'
import TextField from '@material-ui/core/TextField'
import Autocomplete from '@material-ui/lab/Autocomplete'
import Box from '@material-ui/core/Box'
import BuildIcon from '@material-ui/icons/Build'
import TrendingUpIcon from '@material-ui/icons/TrendingUp'
import SaveIcon from '@material-ui/icons/Save'
import CheckCircleIcon from '@material-ui/icons/CheckCircle'

import MapIcon from '@material-ui/icons/Map'
import ListIcon from '@material-ui/icons/List'
import CloseIcon from '@material-ui/icons/Close'
import IconButton from '@material-ui/core/IconButton'
import StopList from './StopList'
import StopDetailPanel from './StopDetailPanel'
import AddStopModal from './AddStopModal'
import RouteMap from './RouteMap'
const { isRouteStale, calculateEstimatedTimes } = require('./routeHelpers')

const useStyles = makeStyles((theme) => ({
  root: {
    display: 'flex',
    flexDirection: 'column',
    height: '100%',
    overflow: 'hidden',
  },
  headerBar: {
    display: 'flex',
    alignItems: 'center',
    flexWrap: 'wrap',
    gap: theme.spacing(1),
    padding: theme.spacing(1.5, 2),
  },
  headerTitle: {
    fontWeight: 600,
    marginRight: theme.spacing(1),
  },
  chips: {
    display: 'flex',
    gap: theme.spacing(0.5),
    marginRight: 'auto',
  },
  chipActive: {
    backgroundColor: theme.palette.success.light,
    color: theme.palette.success.contrastText,
  },
  chipDraft: {
    backgroundColor: theme.palette.warning.light,
    color: theme.palette.warning.contrastText,
  },
  chipStale: {
    backgroundColor: theme.palette.error.light,
    color: theme.palette.error.contrastText,
  },
  actions: {
    display: 'flex',
    gap: theme.spacing(1),
    flexWrap: 'wrap',
  },
  crewPickerRow: {
    padding: theme.spacing(1.5, 2, 0),
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(2),
  },
  crewPicker: {
    minWidth: 260,
  },
  dateField: {
    minWidth: 160,
  },
  panelContainer: {
    display: 'flex',
    flex: 1,
    overflow: 'hidden',
  },
  stopListPanel: {
    width: '40%',
    minWidth: 300,
    borderRight: `1px solid ${theme.palette.divider}`,
    overflow: 'hidden',
    display: 'flex',
    flexDirection: 'column',
  },
  detailPanel: {
    width: '60%',
    overflow: 'hidden',
    display: 'flex',
    flexDirection: 'column',
  },
  loadingContainer: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    height: '100%',
    padding: theme.spacing(6),
  },
  emptyContainer: {
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    alignItems: 'center',
    height: '100%',
    padding: theme.spacing(6),
    color: theme.palette.text.disabled,
    textAlign: 'center',
  },
  mapContainer: {
    flex: 1,
    minHeight: 400,
    position: 'relative',
  },
  detailOverlay: {
    position: 'absolute',
    top: 8,
    right: 8,
    bottom: 8,
    width: 340,
    maxWidth: '45%',
    zIndex: 10,
    borderRadius: theme.shape.borderRadius,
    boxShadow: theme.shadows[8],
    overflow: 'hidden',
    backgroundColor: theme.palette.background.paper,
  },
}))

function UsersRoute({ url, user }) {
  const classes = useStyles()
  const location = useLocation()

  // Determine if we are on the admin path
  const isAdmin = location.pathname.includes('/admin/routes')

  // ── State ────────────────────────────────────────────────────────────────────
  const [crewId, setCrewId] = useState(null)
  const [crewName, setCrewName] = useState('')
  const [crews, setCrews] = useState([])
  const [route, setRoute] = useState(null)
  const [stops, setStops] = useState([])
  const [selectedStopIndex, setSelectedStopIndex] = useState(null)
  const [loading, setLoading] = useState(false)
  const [saving, setSaving] = useState(false)
  const [dirty, setDirty] = useState(false)
  const [addModalOpen, setAddModalOpen] = useState(false)
  const [notice, setNotice] = useState(null)
  const [buildDate, setBuildDate] = useState(() => {
    const d = new Date()
    return d.toISOString().split('T')[0]
  })
  const [startLocation, setStartLocation] = useState(null)
  const [endLocation, setEndLocation] = useState(null)
  const [returnToStart, setReturnToStart] = useState(true)
  const [viewMode, setViewMode] = useState('list') // 'list' or 'map' or 'split'
  const [leaveAtTime, setLeaveAtTime] = useState('06:00') // HH:MM for the time picker

  // Yard options for start/end location picker
  const YARD_OPTIONS = [
    { label: 'DFL Yard (Carefree)', identifier: 'DFL_Yard', latitude: 33.81975791933278, longitude: -111.95411562884613, address: '36815 N Cave Creek Rd, Cave Creek, AZ 85331' },
    { label: 'Benhart Yard', identifier: 'Benhart_Yard', latitude: 33.6791423513395, longitude: -112.03677693007101, address: '2112 E Quail Ave, Phoenix, AZ 85028' },
    { label: 'HMI Yard', identifier: 'HMI_Yard', latitude: 33.547592, longitude: -112.179457, address: '4020 W Mohave St, Phoenix, AZ 85009' },
  ]

  // ── Helpers ──────────────────────────────────────────────────────────────────

  const showNotice = useCallback((severity, message) => {
    setNotice({ severity, message })
  }, [])

  const selectedStop = selectedStopIndex != null ? stops[selectedStopIndex] || null : null

  // Build a full leaveAt Date from the date + time picker
  const leaveAt = (() => {
    const [hh, mm] = (leaveAtTime || '06:00').split(':').map(Number)
    const d = new Date(buildDate || Date.now())
    d.setHours(hh || 6, mm || 0, 0, 0)
    return d
  })()

  // Compute estimated times for display
  const stopsWithTimes = calculateEstimatedTimes(stops, leaveAt)

  // ── On mount: resolve crew ───────────────────────────────────────────────────

  useEffect(() => {
    if (isAdmin) {
      // Load all crews for the admin picker
      axios
        .get(`${url}/api/crews/listCrews`)
        .then((res) => {
          const list = (res.data && res.data.crews) || []
          setCrews(list)
        })
        .catch((err) => {
          console.error('[UsersRoute] failed to load crews', err)
          showNotice('error', 'Failed to load crews.')
        })
    } else {
      // Resolve current user's crew
      setLoading(true)
      axios
        .get(`${url}/api/mobileapp/loadInfoForMobileApp`)
        .then((res) => {
          const data = res.data || {}
          const crew = data.crew
          const employee = data.employee
          if (crew && crew._id) {
            setCrewId(crew._id)
            setCrewName(crew.name || crew.foremanName || 'My Crew')
          } else if (employee && (employee.currentCrew || employee.crew)) {
            setCrewId(employee.currentCrew || employee.crew)
            setCrewName('My Crew')
          } else {
            showNotice('warning', 'No crew found for your account.')
          }
        })
        .catch((err) => {
          console.error('[UsersRoute] failed to load user info', err)
          showNotice('error', 'Failed to load your crew information.')
        })
        .finally(() => setLoading(false))
    }
  }, [isAdmin, url, showNotice])

  // ── When crewId changes: fetch active route ──────────────────────────────────

  useEffect(() => {
    if (!crewId) return

    setLoading(true)
    setSelectedStopIndex(null)
    setDirty(false)

    axios
      .get(`${url}/api/mobileapp/user-routes/active`, { params: { crewId } })
      .then((res) => {
        const r = (res.data && res.data.route) || res.data || null
        setRoute(r)
        setStops(r && r.stops ? r.stops : [])
        if (r && r.startLocation) setStartLocation(r.startLocation)
        if (r && r.endLocation) setEndLocation(r.endLocation)
        if (r && r.returnToStart != null) setReturnToStart(r.returnToStart)
        if (r && r.leaveAt) {
          const d = new Date(r.leaveAt)
          if (!isNaN(d.getTime())) {
            setLeaveAtTime(`${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`)
          }
        }
      })
      .catch((err) => {
        // 404 means no active route yet — that is fine
        if (err.response && err.response.status === 404) {
          setRoute(null)
          setStops([])
        } else {
          console.error('[UsersRoute] failed to load active route', err)
          showNotice('error', 'Failed to load active route.')
        }
      })
      .finally(() => setLoading(false))
  }, [crewId, url, showNotice])

  // ── Action handlers ──────────────────────────────────────────────────────────

  const handleBuildFromSchedule = async () => {
    if (!crewId) return showNotice('warning', 'Select a crew first.')
    if (!buildDate) return showNotice('warning', 'Select a date first.')

    setLoading(true)
    try {
      const res = await axios.post(`${url}/api/mobileapp/user-routes/build-from-schedule`, {
        crewId,
        date: buildDate,
        source: 'all',
      })
      const r = res.data?.route || res.data || {}
      setRoute(r)
      setStops(r.stops || [])
      if (r.startLocation) setStartLocation(r.startLocation)
      if (r.endLocation) setEndLocation(r.endLocation)
      setSelectedStopIndex(null)
      setDirty(false)
      showNotice('success', `Route built with ${(r.stops || []).length} stops.`)
    } catch (err) {
      console.error('[UsersRoute] build from schedule failed', err)
      const msg =
        (err.response && err.response.data && err.response.data.message) ||
        'Failed to build route from schedule.'
      showNotice('error', msg)
    } finally {
      setLoading(false)
    }
  }

  const handleSave = async () => {
    if (!route || !route._id) return showNotice('warning', 'No route to save.')

    setSaving(true)
    try {
      const res = await axios.put(`${url}/api/mobileapp/user-routes/${route._id}`, {
        stops,
        startLocation,
        endLocation: returnToStart ? startLocation : endLocation,
        returnToStart,
        leaveAt: leaveAt.toISOString(),
      })
      const updated = res.data?.route || res.data || {}
      setRoute(updated)
      setStops(updated.stops || stops)
      setDirty(false)
      showNotice('success', 'Route saved.')
    } catch (err) {
      console.error('[UsersRoute] save failed', err)
      const msg =
        (err.response && err.response.data && err.response.data.message) ||
        'Failed to save route.'
      showNotice('error', msg)
    } finally {
      setSaving(false)
    }
  }

  const handleOptimize = async () => {
    if (!route || !route._id) return showNotice('warning', 'No route to optimize.')
    if (stops.length < 2) return showNotice('info', 'Need at least 2 stops to optimize.')

    setLoading(true)
    try {
      // Step 1: Get optimized order
      if (!startLocation || !startLocation.latitude) {
        setLoading(false)
        return showNotice('warning', 'Set a start location before optimizing.')
      }

      const endLoc = returnToStart ? startLocation : (endLocation || startLocation)

      const optimizeRes = await axios.post(`${url}/api/mobileapp/user-routes/optimize`, {
        crewId,
        routeId: route._id,
        stops,
        startLocation,
        endLocation: endLoc,
        returnToStart,
        leaveAt: leaveAt.toISOString(),
      })
      const optData = optimizeRes.data || {}
      // Prefer dayPlan stops (have plannedDriveMinutes from traffic timing) over raw orderedStops
      const dayPlanStops = optData.dayPlans?.[0]?.stops
      const orderedStops = dayPlanStops || optData.orderedStops || stops

      // Capture return drive time from day plan
      const endLegMinutes = optData.dayPlans?.[0]?.endLegMinutes ?? null

      // Step 2: Save the optimized order + optimization result
      const saveRes = await axios.put(`${url}/api/mobileapp/user-routes/${route._id}`, {
        stops: orderedStops,
        lastOptimizedAt: new Date().toISOString(),
        optimizationResult: {
          dayPlans: optData.dayPlans || [],
          summary: optData.summary || {},
          endLegMinutes,
        },
      })
      const updated = saveRes.data?.route || saveRes.data || {}
      // Ensure optimizationResult is on the route even if the save response doesn't echo it
      setRoute({
        ...updated,
        optimizationResult: updated.optimizationResult || {
          dayPlans: optData.dayPlans || [],
          summary: optData.summary || {},
          endLegMinutes,
        }
      })
      setStops(updated.stops || orderedStops)
      setSelectedStopIndex(null)
      setDirty(false)
      showNotice('success', 'Route optimized and saved.')
    } catch (err) {
      console.error('[UsersRoute] optimize failed', err)
      const msg =
        (err.response && err.response.data && err.response.data.message) ||
        'Failed to optimize route.'
      showNotice('error', msg)
    } finally {
      setLoading(false)
    }
  }

  const handleActivate = async () => {
    if (!route || !route._id) return showNotice('warning', 'No route to activate.')

    const confirmed = window.confirm(
      'Activate this route? It will become the live route for the crew.'
    )
    if (!confirmed) return

    try {
      const res = await axios.post(`${url}/api/mobileapp/user-routes/${route._id}/activate`)
      const updated = res.data?.route || res.data || {}
      setRoute(updated)
      showNotice('success', 'Route activated.')
    } catch (err) {
      console.error('[UsersRoute] activate failed', err)
      const msg =
        (err.response && err.response.data && err.response.data.message) ||
        'Failed to activate route.'
      showNotice('error', msg)
    }
  }

  // ── Stop manipulation handlers ───────────────────────────────────────────────

  const handleStopReorder = (newStops) => {
    setStops(newStops)
    setDirty(true)
  }

  const handleStopUpdate = (updatedStop) => {
    setStops((prev) =>
      prev.map((s, i) => (i === selectedStopIndex ? updatedStop : s))
    )
    setDirty(true)
  }

  const handleStopRemove = () => {
    setStops((prev) => prev.filter((_, i) => i !== selectedStopIndex))
    setSelectedStopIndex(null)
    setDirty(true)
  }

  const handleAddStop = (newStop) => {
    setStops((prev) => {
      const ordered = [...prev, { ...newStop, order: prev.length + 1 }]
      return ordered
    })
    setDirty(true)
  }

  const handleSelectStop = (stop) => {
    const idx = stops.findIndex(
      (s) =>
        (s._id && String(s._id) === String(stop._id)) ||
        (s.stopId && s.stopId === stop.stopId)
    )
    setSelectedStopIndex(idx >= 0 ? idx : null)
  }

  // ── Admin crew picker handler ────────────────────────────────────────────────

  const handleCrewChange = (_event, crew) => {
    if (crew && crew._id) {
      setCrewId(crew._id)
      setCrewName(crew.name || crew.foremanName || 'Crew')
    } else {
      setCrewId(null)
      setCrewName('')
      setRoute(null)
      setStops([])
      setSelectedStopIndex(null)
    }
  }

  // ── Render ───────────────────────────────────────────────────────────────────

  const routeStatus = route ? route.status || 'draft' : null
  const stale = isRouteStale(route)

  return (
    <Box className={classes.root}>
      {/* ── Admin crew picker ── */}
      {isAdmin && (
        <Box className={classes.crewPickerRow}>
          <Autocomplete
            className={classes.crewPicker}
            options={crews}
            getOptionLabel={(option) =>
              option.name || option.foremanName || 'Unnamed Crew'
            }
            onChange={handleCrewChange}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Select Crew"
                variant="outlined"
                size="small"
              />
            )}
          />
          <TextField
            className={classes.dateField}
            label="Schedule Date"
            type="date"
            variant="outlined"
            size="small"
            value={buildDate}
            onChange={(e) => setBuildDate(e.target.value)}
            InputLabelProps={{ shrink: true }}
          />
        </Box>
      )}

      {/* ── Header bar ── */}
      <Paper className={classes.headerBar} elevation={1}>
        <Typography variant="h6" className={classes.headerTitle}>
          {crewName || 'Crew Route'}
        </Typography>

        <Box className={classes.chips}>
          {routeStatus === 'active' && (
            <Chip label="Active" size="small" className={classes.chipActive} />
          )}
          {routeStatus === 'draft' && (
            <Chip label="Draft" size="small" className={classes.chipDraft} />
          )}
          {stale && (
            <Chip label="Needs Optimization" size="small" className={classes.chipStale} />
          )}
          {dirty && <Chip label="Unsaved" size="small" color="secondary" />}
          {stops.length > 0 && (
            <Chip label={`${stops.length} stops`} size="small" variant="outlined" />
          )}
        </Box>

        <Box className={classes.actions}>
          {!isAdmin && (
            <TextField
              className={classes.dateField}
              label="Schedule Date"
              type="date"
              variant="outlined"
              size="small"
              value={buildDate}
              onChange={(e) => setBuildDate(e.target.value)}
              InputLabelProps={{ shrink: true }}
            />
          )}
          <TextField
            label="Start Time"
            type="time"
            variant="outlined"
            size="small"
            value={leaveAtTime}
            onChange={(e) => {
              setLeaveAtTime(e.target.value)
              setDirty(true)
            }}
            InputLabelProps={{ shrink: true }}
            inputProps={{ step: 300 }}
            style={{ minWidth: 120 }}
          />
          <Button
            variant="outlined"
            size="small"
            startIcon={<BuildIcon />}
            onClick={handleBuildFromSchedule}
            disabled={loading || !crewId}
          >
            Build from Schedule
          </Button>
          <Button
            variant="outlined"
            size="small"
            startIcon={<TrendingUpIcon />}
            onClick={handleOptimize}
            disabled={loading || !route || stops.length < 2}
          >
            Optimize
          </Button>
          <Button
            variant="contained"
            size="small"
            color="primary"
            startIcon={saving ? <CircularProgress size={16} /> : <SaveIcon />}
            onClick={handleSave}
            disabled={saving || !route || !dirty}
          >
            Save
          </Button>
          <Button
            variant="outlined"
            size="small"
            color="default"
            startIcon={<CheckCircleIcon />}
            onClick={handleActivate}
            disabled={loading || !route || routeStatus === 'active'}
          >
            Activate
          </Button>

          {/* View mode toggle */}
          {route && (
            <Box style={{ display: 'flex', gap: 2, marginLeft: 8, borderLeft: '1px solid #ccc', paddingLeft: 8 }}>
              <Button
                size="small"
                variant={viewMode === 'list' ? 'contained' : 'outlined'}
                onClick={() => setViewMode('list')}
                style={{ minWidth: 36, padding: '4px 8px' }}
              >
                <ListIcon fontSize="small" />
              </Button>
              <Button
                size="small"
                variant={viewMode === 'split' ? 'contained' : 'outlined'}
                onClick={() => setViewMode('split')}
                style={{ minWidth: 36, padding: '4px 8px' }}
              >
                <ListIcon fontSize="small" style={{ marginRight: 2 }} /><MapIcon fontSize="small" />
              </Button>
              <Button
                size="small"
                variant={viewMode === 'map' ? 'contained' : 'outlined'}
                onClick={() => setViewMode('map')}
                style={{ minWidth: 36, padding: '4px 8px' }}
              >
                <MapIcon fontSize="small" />
              </Button>
            </Box>
          )}
        </Box>
      </Paper>

      {/* ── Start / End Location ── */}
      {route && (
        <Paper elevation={0} style={{ padding: '8px 16px', display: 'flex', alignItems: 'center', gap: 16, flexWrap: 'wrap', borderBottom: '1px solid #e0e0e0' }}>
          <Autocomplete
            freeSolo
            options={YARD_OPTIONS}
            getOptionLabel={(o) => {
              if (typeof o === 'string') return o
              return o.label || o.address || ''
            }}
            value={
              startLocation
                ? (YARD_OPTIONS.find(y => startLocation.yardId && y.identifier === startLocation.yardId) || startLocation.label || startLocation.address || '')
                : ''
            }
            onChange={(_, val) => {
              if (val && typeof val === 'object' && val.identifier) {
                // Selected a yard from dropdown
                const loc = { type: 'yard', label: val.label, address: val.address, latitude: val.latitude, longitude: val.longitude, yardId: val.identifier }
                setStartLocation(loc)
                if (returnToStart) setEndLocation(loc)
              } else if (!val) {
                setStartLocation(null)
              }
              setDirty(true)
            }}
            onKeyDown={async (e) => {
              if (e.key === 'Enter') {
                const typed = e.target.value
                if (!typed || typed.length < 3) return
                // Check if it matches a yard option — if not, geocode
                const yardMatch = YARD_OPTIONS.find(y => y.label.toLowerCase() === typed.toLowerCase())
                if (yardMatch) return // let Autocomplete handle it
                e.preventDefault()
                try {
                  const res = await axios.post(`${url}/api/mobileapp/user-routes/geocode-stop`, { address: typed })
                  const geo = res.data?.stop || res.data || {}
                  const loc = {
                    type: 'manual_address',
                    label: typed,
                    address: typed,
                    latitude: geo.latitude || null,
                    longitude: geo.longitude || null,
                  }
                  setStartLocation(loc)
                  if (returnToStart) setEndLocation(loc)
                  setDirty(true)
                  if (geo.latitude) {
                    showNotice('success', `Start location geocoded: ${typed}`)
                  } else {
                    showNotice('warning', `Could not geocode "${typed}" — optimization may fail.`)
                  }
                } catch (err) {
                  showNotice('error', 'Failed to geocode address.')
                }
              }
            }}
            size="small"
            style={{ minWidth: 280 }}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Start Location"
                variant="outlined"
                placeholder="Select yard or type address + Enter"
              />
            )}
          />
          <Autocomplete
            freeSolo
            options={YARD_OPTIONS}
            getOptionLabel={(o) => {
              if (typeof o === 'string') return o
              return o.label || o.address || ''
            }}
            value={
              endLocation
                ? (YARD_OPTIONS.find(y => endLocation.yardId && y.identifier === endLocation.yardId) || endLocation.label || endLocation.address || '')
                : ''
            }
            onChange={(_, val) => {
              if (val && typeof val === 'object' && val.identifier) {
                setEndLocation({ type: 'yard', label: val.label, address: val.address, latitude: val.latitude, longitude: val.longitude, yardId: val.identifier })
                setReturnToStart(false)
              } else if (!val) {
                setEndLocation(null)
              }
              setDirty(true)
            }}
            onKeyDown={async (e) => {
              if (e.key === 'Enter') {
                const typed = e.target.value
                if (!typed || typed.length < 3) return
                const yardMatch = YARD_OPTIONS.find(y => y.label.toLowerCase() === typed.toLowerCase())
                if (yardMatch) return
                e.preventDefault()
                try {
                  const res = await axios.post(`${url}/api/mobileapp/user-routes/geocode-stop`, { address: typed })
                  const geo = res.data?.stop || res.data || {}
                  setEndLocation({
                    type: 'manual_address',
                    label: typed,
                    address: typed,
                    latitude: geo.latitude || null,
                    longitude: geo.longitude || null,
                  })
                  setReturnToStart(false)
                  setDirty(true)
                  if (geo.latitude) {
                    showNotice('success', `End location geocoded: ${typed}`)
                  } else {
                    showNotice('warning', `Could not geocode "${typed}" — optimization may fail.`)
                  }
                } catch (err) {
                  showNotice('error', 'Failed to geocode address.')
                }
              }
            }}
            disabled={returnToStart}
            size="small"
            style={{ minWidth: 280 }}
            renderInput={(params) => (
              <TextField
                {...params}
                label="End Location"
                variant="outlined"
                placeholder="Select yard or type address + Enter"
              />
            )}
          />
          <label style={{ display: 'flex', alignItems: 'center', cursor: 'pointer', fontSize: '0.875rem' }}>
            <input
              type="checkbox"
              checked={returnToStart}
              onChange={(e) => {
                setReturnToStart(e.target.checked)
                if (e.target.checked && startLocation) setEndLocation(startLocation)
                setDirty(true)
              }}
              style={{ marginRight: 4 }}
            />
            Return to start
          </label>
        </Paper>
      )}

      {/* ── Main content ── */}
      {loading ? (
        <Box className={classes.loadingContainer}>
          <CircularProgress />
        </Box>
      ) : !crewId ? (
        <Box className={classes.emptyContainer}>
          <Typography variant="h6">No Crew Selected</Typography>
          <Typography variant="body2">
            {isAdmin
              ? 'Choose a crew from the picker above to get started.'
              : 'Unable to determine your crew. Please contact an administrator.'}
          </Typography>
        </Box>
      ) : (
        <Box className={classes.panelContainer}>
          {/* Stop List — shown in list and split modes */}
          {(viewMode === 'list' || viewMode === 'split') && (
            <Box className={classes.stopListPanel} style={{ width: viewMode === 'split' ? '30%' : '40%' }}>
              <StopList
                stops={stopsWithTimes}
                selectedStop={selectedStop}
                onSelectStop={handleSelectStop}
                onReorder={handleStopReorder}
                onAddStop={() => setAddModalOpen(true)}
                yardName={crewName || 'Yard'}
                leaveAt={leaveAt}
                endLegMinutes={route?.optimizationResult?.endLegMinutes ?? null}
              />
            </Box>
          )}

          {/* Detail Panel — shown in list mode only */}
          {viewMode === 'list' && (
            <Box className={classes.detailPanel}>
              <StopDetailPanel
                key={selectedStopIndex ?? 'none'}
                stop={
                  selectedStopIndex != null
                    ? stopsWithTimes[selectedStopIndex] || null
                    : null
                }
                onUpdate={handleStopUpdate}
                onRemove={handleStopRemove}
                baseUrl={url}
              />
            </Box>
          )}

          {/* Map — shown in map and split modes */}
          {(viewMode === 'map' || viewMode === 'split') && (
            <Box className={classes.mapContainer}>
              <RouteMap
                stops={stopsWithTimes}
                selectedIndex={selectedStopIndex}
                onSelectStop={(idx) => {
                  setSelectedStopIndex(idx)
                  if (viewMode === 'map') setViewMode('split')
                }}
                startLocation={startLocation}
                endLocation={returnToStart ? startLocation : endLocation}
                baseUrl={url}
              />
              {/* Detail overlay — shown in split/map mode when a stop is selected */}
              {viewMode === 'split' && selectedStopIndex != null && (
                <Box className={classes.detailOverlay}>
                  <IconButton
                    size="small"
                    onClick={() => setSelectedStopIndex(null)}
                    style={{ position: 'absolute', top: 4, right: 4, zIndex: 11, backgroundColor: 'rgba(255,255,255,0.8)' }}
                  >
                    <CloseIcon fontSize="small" />
                  </IconButton>
                  <StopDetailPanel
                    key={selectedStopIndex ?? 'none'}
                    stop={stopsWithTimes[selectedStopIndex] || null}
                    onUpdate={handleStopUpdate}
                    onRemove={handleStopRemove}
                    baseUrl={url}
                  />
                </Box>
              )}
            </Box>
          )}
        </Box>
      )}

      {/* ── Add Stop Modal ── */}
      <AddStopModal
        open={addModalOpen}
        onClose={() => setAddModalOpen(false)}
        onAddStop={handleAddStop}
        baseUrl={url}
      />

      {/* ── Snackbar ── */}
      <Snackbar
        open={!!notice}
        autoHideDuration={4000}
        onClose={() => setNotice(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        {notice ? (
          <Alert
            onClose={() => setNotice(null)}
            severity={notice.severity}
            variant="filled"
            elevation={6}
          >
            {notice.message}
          </Alert>
        ) : undefined}
      </Snackbar>
    </Box>
  )
}

const mapStateToProps = (state) => ({
  url: state.url,
  user: state.user,
  authenticated: state.authenticated,
})

export default connect(mapStateToProps)(UsersRoute)
